import { ReactNode, ComponentPropsWithoutRef, forwardRef, ElementRef } from 'react';
import { CSS } from '@stitches/react';

import {
  COLORS,
  ColorStyles,
  ColorType,
  SizeStyles,
  SizeType,
  styled,
  WeightStyles,
  WeightType,
} from '../../../stitches.config';
import { Icon } from '../..';

function getVariants() {
  const variants = {
    weight: {} as WeightStyles,
    size: {
      small: {
        gap: '$2',
        borderRadius: '$small',
        padding: '$2 $3',
        fontSize: '$2',
      },
      medium: {
        gap: '$3',
        borderRadius: '$medium',
        padding: '$3 $4',
        fontSize: '$3',
      },
      large: {
        gap: '$4',
        borderRadius: '$medium',
        padding: '$4 $5',
        fontSize: '$4',
      },
    } as SizeStyles,
    color1: {} as ColorStyles,
    color2: {} as ColorStyles,
    hasText: {
      false: {
        padding: '$3 $3',
      },
    },
    circle: {
      true: {
        borderRadius: '$full',
      },
    },
  };

  const compoundVariants = [
    // Padding depends on whether button has text or not. Custom setting apply to the outlined weight.
    ...Object.entries(variants.size).flatMap(([size, css]: [string, any]) => {
      const [paddingV, paddingH] = css.padding.split(' ');
      return [
        {
          size,
          hasText: false,
          css: { padding: `${paddingV} ${paddingV}` },
        },
        {
          size,
          hasText: true,
          css: { padding: `${paddingV} ${paddingH}` },
        },
        {
          weight: 'outlined',
          size,
          hasText: false,
          css: {
            padding: `calc(${paddingV} - 1px) calc(${paddingV} - 1px) !important`,
          },
        },
        {
          weight: 'outlined',
          size,
          hasText: true,
          css: {
            padding: `calc(${paddingV} - 1px) calc(${paddingH} - 1px) !important`,
          },
        },
      ];
    }),
    // Variants by color
    ...COLORS.flatMap(color1 => [
      {
        weight: 'solid',
        color1,
        css: {
          'backgroundColor': `$${color1}9`,
          'color': `$${color1}1`,
          '&:hover, &:focus-visible': {
            backgroundColor: `$${color1}10`,
          },
          '&:focus-visible': {
            outline: `2px solid $${color1}7`,
          },
          '&:active': {
            backgroundColor: `$${color1}11`,
          },
          '&:disabled': {
            backgroundColor: `$${color1}7`,
          },
          '&[data-state="open"]': {
            backgroundColor: `$${color1}11`,
          },
        },
      },
      {
        weight: 'solid-light',
        color1,
        css: {
          'backgroundColor': `$${color1}5`,
          'color': `$${color1}11`,
          '&:hover, &:focus-visible': {
            backgroundColor: `$${color1}6`,
          },
          '&:focus-visible': {
            outline: `2px solid $${color1}4`,
          },
          '&:active, &[data-active=true]': {
            backgroundColor: `$${color1}7`,
          },
          '&:disabled': {
            backgroundColor: `$${color1}4`,
            color: `$${color1}6`,
          },
          '&[data-state="open"]': {
            backgroundColor: `$${color1}8`,
          },
        },
      },
      {
        weight: 'outlined',
        color1,
        css: {
          'backgroundColor': 'transparent',
          'borderWidth': '1px',
          'borderStyle': 'solid',
          'borderColor': `$${color1}8`,
          'color': `$${color1}11`,
          '&:hover, &:focus-visible': {
            backgroundColor: `$${color1}A3`,
            borderColor: `$${color1}9`,
            color: `$${color1}11`,
          },
          '&:focus-visible': {
            outline: `2px solid $${color1}7`,
          },
          '&:active, &[data-active=true]': {
            backgroundColor: `$${color1}4`,
          },
          '&:disabled': {
            backgroundColor: 'transparent',
            borderColor: `$${color1}5`,
            color: `$${color1}7`,
          },
          '&[data-state="open"]': {
            backgroundColor: `$${color1}6`,
          },
        },
      },
      {
        weight: 'minimal',
        color1,
        css: {
          'backgroundColor': 'transparent',
          'color': `$${color1}11`,
          '&:hover': {
            backgroundColor: `$${color1}A4`,
            color: `$${color1}11`,
          },
          '&:focus-visible': {
            outline: `2px solid $${color1}7`,
          },
          '&:active': {
            backgroundColor: `$${color1}A6`,
          },
          '&:disabled': {
            backgroundColor: 'transparent',
            color: `$${color1}8`,
          },
          '&[data-active=true]': {
            backgroundColor: `$${color1}A5`,
            color: `$${color1}10`,
          },
          '&[data-state="open"]': {
            backgroundColor: `$${color1}6`,
          },
        },
      },
      // Variants by a combination of two colors
      ...COLORS.filter(color2 => color1 !== color2).map(color2 => ({
        weight: 'solid',
        color1,
        color2,
        css: {
          'background': `linear-gradient(135deg, $${color1}9, $${color2}9)`,
          'color': `$${color1}1`,
          '&:hover, &:focus-visible': {
            background: `linear-gradient(135deg, $${color1}10, $${color2}10)`,
          },
          '&:focus-visible': {
            outline: `2px solid $${color1}7`,
          },
          '&:active, &[data-active=true]': {
            background: `linear-gradient(135deg, $${color1}11, $${color2}11)`,
          },
          '&:disabled': {
            background: `linear-gradient(135deg, $${color1}7, $${color2}7)`,
          },
        },
      })),
      ...COLORS.filter(color2 => color1 !== color2).map(color2 => ({
        weight: 'solid-light',
        color1,
        color2,
        css: {
          'background': `linear-gradient(135deg, $${color1}4, $${color2}4)`,
          'color': `$${color1}11`,
          '&:hover, &:focus-visible': {
            background: `linear-gradient(135deg, $${color1}5, $${color2}5)`,
          },
          '&:focus-visible': {
            outline: `2px solid $${color1}7`,
          },
          '&:active, &[data-active=true]': {
            background: `linear-gradient(135deg, $${color1}5, $${color2}5)`,
          },
          '&:disabled': {
            background: `linear-gradient(135deg, $${color1}4, $${color2}4)`,
          },
        },
      })),
    ]),
  ];

  return {
    variants,
    compoundVariants,
  };
}

const StyledButton = styled('button', {
  'display': 'inline-flex',
  'justifyContent': 'center',
  'alignItems': 'center',
  'maxWidth': '100%',
  'border': 0,
  'outline': 'none',
  'fontSize': '$3',
  'textDecoration': 'none',
  'cursor': 'pointer',
  '&:disabled': {
    cursor: 'not-allowed',
  },
  ...getVariants(),
});

const Text = styled('span', {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

export interface ButtonProps extends ComponentPropsWithoutRef<'button'> {
  size?: SizeType;
  weight?: WeightType;
  theme?: ColorType | [ColorType, ColorType];
  text?: ReactNode;
  icon?: string;
  rightIcon?: string;
  circle?: boolean;
  active?: boolean;
  as?: any;
  css?: CSS;
  iconCss?: CSS;
}

export const Button = forwardRef<ElementRef<typeof StyledButton>, ButtonProps>((props, forwardedRef) => {
  const {
    size = 'medium',
    weight = 'solid',
    theme = 'slate',
    text,
    icon,
    rightIcon,
    active,
    iconCss,
    ...restProps
  } = props;

  return (
    <StyledButton
      size={size}
      weight={weight}
      color1={Array.isArray(theme) ? theme[0] : theme}
      color2={Array.isArray(theme) ? theme[1] : undefined}
      hasText={Boolean(text)}
      data-active={active}
      ref={forwardedRef}
      {...restProps}
    >
      {icon && <Icon icon={icon} size={size} css={iconCss} />}

      {text && <Text>{text}</Text>}

      {rightIcon && <Icon icon={rightIcon} size={size} />}
    </StyledButton>
  );
});
